import React, { useEffect, useState, useCallback } from 'react';
import { HiOutlineUsers, HiOutlineShieldCheck, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import AppLayout from '../../components/layout/AppLayout';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import StatCard from '../../components/ui/StatCard';
import FormInput from '../../components/forms/FormInput';
import FormSelect from '../../components/forms/FormSelect';
import { useAuth } from '../../contexts/AuthContext';
import { getAllUsers, createUser, updateUser, deleteUser, getUserPermissions, setUserPermissions, clearUserPermissions } from '../../api/auth';

const roleOptions = [
  { value: 'admin',          label: 'Admin' },
  { value: 'caretaker',      label: 'Care Taker' },
  { value: 'gas_reader',     label: 'Gas Reader' },
  { value: 'security_guard', label: 'Security Guard' },
  { value: 'custom',         label: 'Custom Role...' },
];

const roleBadge = {
  superadmin:     'bg-purple-100 text-purple-800',
  admin:          'bg-blue-100 text-blue-800',
  caretaker:      'bg-green-100 text-green-800',
  gas_reader:     'bg-orange-100 text-orange-800',
  security_guard: 'bg-yellow-100 text-yellow-800',
  custom:         'bg-gray-100 text-gray-700',
};

const roleLabel = {
  superadmin:     'Super Admin',
  admin:          'Admin',
  caretaker:      'Care Taker',
  gas_reader:     'Gas Reader',
  security_guard: 'Security Guard',
  custom:         'Custom',
};

const featureLabels = {
  dashboard:              'Dashboard',
  calendar:               'Calendar',
  notifications:          'Notifications',
  contracts:              'Contracts (MOUs)',
  quotations:             'Quotations',
  services:               'Work Management',
  staff:                  'Staff Allocation',
  security:               'Security',
  cleaning:               'Cleaning',
  housekeeping:           'Housekeeping',
  water_treatment:        'Water Treatment',
  gas_readings:           'Gas Readings',
  gas_reading_settings:   'Gas Reading Settings',
  apartment_management:   'Apartment Management',
  visitors:               'Visitors',
  members:                'Apartment Members',
  bookings:               'Facility Bookings',
  payments:               'Payment Tracker',
  facility_management:    'Facility Management & Compliance',
};


const emptyForm = { name: '', email: '', password: '', role: 'caretaker', custom_role: '' };

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [permUser, setPermUser] = useState(null); // user whose permissions are being edited


  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllUsers();
      setUsers(res.users || res.data || res || []);
    } catch (err) {
      console.error('Failed to load users', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({ name: user.name, email: user.email, password: '', role: user.role, custom_role: user.custom_role || '' });
    setErrors({});
    setShowModal(true);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        custom_role: form.role === 'custom' ? form.custom_role : null,
      };
      if (editing) {
        if (form.password) payload.password = form.password;
        await updateUser(editing.id, payload);
      } else {
        payload.password = form.password;
        await createUser(payload);
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 422) {
        setErrors(err.response.data.errors || {});
      } else {
        alert(err.response?.data?.message || 'Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser(deleteTarget.id);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isAdmin = currentUser?.role === 'admin';
  const canManageUser = (u) => !(isAdmin && u.role === 'superadmin');

  const userList = Array.isArray(users) ? users : [];
  const adminCount = userList.filter((u) => u.role === 'admin').length;
  const caretakerCount = userList.filter((u) => u.role === 'caretaker').length;
  const gasReaderCount = userList.filter((u) => u.role === 'gas_reader').length;

  return (
    <AppLayout title="User Management" breadcrumbs="Users">
      <div className="space-y-6">
        <>
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Total Users" value={userList.length} icon={HiOutlineUsers} color="bg-gray-100 text-gray-700" />
              <StatCard title="Admins" value={adminCount} icon={HiOutlineShieldCheck} color="bg-blue-100 text-blue-700" />
              <StatCard title="Care Takers" value={caretakerCount} icon={HiOutlineUsers} color="bg-green-100 text-green-700" />
              <StatCard title="Gas Readers" value={gasReaderCount} icon={HiOutlineUsers} color="bg-orange-100 text-orange-700" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">All Users</h2>
              <Button onClick={openCreate}>Add User</Button>
            </div>

            {/* Content */}
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading users...</div>
            ) : userList.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No users found</div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userList.map((u) => (
                        <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{u.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{u.email}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${roleBadge[u.role] || 'bg-gray-100 text-gray-800'}`}>
                              {u.role === 'custom' && u.custom_role ? u.custom_role : (roleLabel[u.role] || u.role)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {!(u.role === 'superadmin' && u.id === currentUser?.id) && canManageUser(u) && (
                                <button onClick={() => setPermUser(u)}
                                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors">
                                  <HiOutlineShieldCheck className="w-3.5 h-3.5" />
                                  Permissions
                                </button>
                              )}
                              {canManageUser(u) && (
                                <button onClick={() => openEdit(u)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                                  <HiOutlinePencil className="w-4 h-4" />
                                </button>
                              )}
                              {u.id !== currentUser?.id && canManageUser(u) && (
                                <button onClick={() => setDeleteTarget(u)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600">
                                  <HiOutlineTrash className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden grid grid-cols-1 gap-4">
                  {userList.map((u) => (
                    <div key={u.id} className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{u.name}</h3>
                          <p className="text-sm text-gray-500 mt-0.5">{u.email}</p>
                        </div>
                        <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${roleBadge[u.role] || 'bg-gray-100 text-gray-800'}`}>
                          {u.role === 'custom' && u.custom_role ? u.custom_role : (roleLabel[u.role] || u.role)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                      </p>
                      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end gap-2">
                        {!(u.role === 'superadmin' && u.id === currentUser?.id) && canManageUser(u) && (
                          <Button variant="secondary" size="sm" onClick={() => setPermUser(u)}>Permissions</Button>
                        )}
                        {canManageUser(u) && (
                          <Button variant="secondary" size="sm" onClick={() => openEdit(u)}>Edit</Button>
                        )}
                        {u.id !== currentUser?.id && canManageUser(u) && (
                          <Button variant="danger" size="sm" onClick={() => setDeleteTarget(u)}>Delete</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete User" size="sm">
        <div className="text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <HiOutlineTrash className="w-7 h-7 text-red-500" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Are you sure you want to delete</p>
            <p className="font-semibold text-gray-900 mt-1">{deleteTarget?.name}</p>
            <p className="text-xs text-gray-400 mt-1">This action cannot be undone.</p>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="danger" className="flex-1" onClick={handleDelete}>Delete</Button>
          </div>
        </div>
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit User' : 'Add User'} size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput label="Name" name="name" value={form.name} onChange={handleChange} error={errors.name?.[0]} required />
          <FormInput label="Email" name="email" type="email" value={form.email} onChange={handleChange} error={errors.email?.[0]} required />
          <FormInput
            label={editing ? 'Password (leave blank to keep current)' : 'Password'}
            name="password"
            type="password"
            value={form.password}
            onChange={handleChange}
            error={errors.password?.[0]}
            required={!editing}
          />
          <FormSelect label="Role" name="role" value={form.role} onChange={handleChange} options={roleOptions} error={errors.role?.[0]} required />
          {form.role === 'custom' && (
            <FormInput
              label="Custom Role Name"
              name="custom_role"
              value={form.custom_role}
              onChange={handleChange}
              placeholder="e.g. Plumber, Electrician, Watchman..."
              error={errors.custom_role?.[0]}
              required
            />
          )}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" loading={submitting}>{editing ? 'Update User' : 'Add User'}</Button>
          </div>
        </form>
      </Modal>

      {/* Per-User Permission Modal */}
      {permUser && (
        <UserPermissionModal
          user={permUser}
          onClose={() => setPermUser(null)}
          onSaved={() => { setPermUser(null); fetchUsers(); }}
        />
      )}
    </AppLayout>
  );
}

// ─── Per-User Permission Modal ───────────────────────────────────────────────

function UserPermissionModal({ user, onClose, onSaved }) {
  const [perms, setPerms]       = useState({});
  const [hasCustom, setHasCustom] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    getUserPermissions(user.id).then(data => {
      setHasCustom(data.has_custom);
      // If has custom perms, use them; else seed from allowed_features
      if (data.has_custom) {
        setPerms(data.permissions);
      } else {
        // seed from current allowed_features as defaults
        const seeded = {};
        Object.keys(featureLabels).forEach(f => {
          seeded[f] = (user.allowed_features || []).includes(f);
        });
        setPerms(seeded);
      }
    }).finally(() => setLoading(false));
  }, [user]);

  const toggle = (f) => setPerms(p => ({ ...p, [f]: !p[f] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await setUserPermissions(user.id, perms);
      onSaved();
    } finally { setSaving(false); }
  };

  const handleClear = async () => {
    if (!window.confirm('Remove custom permissions? This user will inherit role defaults.')) return;
    await clearUserPermissions(user.id);
    onSaved();
  };

  const enabledCount = Object.values(perms).filter(Boolean).length;

  return (
    <Modal isOpen onClose={onClose} title="Set User Permissions" size="md">
      <div className="space-y-4">
        {/* User info */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#032841,#043959)' }}>
            {user.name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800">{user.name}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
          {hasCustom && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
              Custom
            </span>
          )}
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">Loading permissions...</div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Features — {enabledCount} enabled
              </p>
              <div className="flex gap-2">
                <button onClick={() => setPerms(Object.fromEntries(Object.keys(featureLabels).map(f => [f, true])))}
                  className="text-xs text-blue-600 hover:underline">All</button>
                <span className="text-gray-300">|</span>
                <button onClick={() => setPerms(Object.fromEntries(Object.keys(featureLabels).map(f => [f, false])))}
                  className="text-xs text-gray-500 hover:underline">None</button>
              </div>
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
              {Object.entries(featureLabels).map(([feature, label]) => (
                <div key={feature} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
                  <span className="text-sm text-gray-700">{label}</span>
                  <button onClick={() => toggle(feature)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      perms[feature] ? 'bg-blue-600' : 'bg-gray-200'
                    }`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      perms[feature] ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3 pt-1">
              {hasCustom && (
                <button onClick={handleClear}
                  className="text-xs text-red-500 hover:text-red-700 hover:underline mr-auto">
                  Reset to role defaults
                </button>
              )}
              <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
              <Button loading={saving} onClick={handleSave}>Save Permissions</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
